// src/pages/customers/CustomerExpiryTabs.tsx
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  Router,
  Search,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_CONFIG, fetchApp } from "../../services/apiService";

type ExpiryTabKey = "expired" | "1" | "2" | "3";

interface ExpiryCustomer {
  secret_id: string;
  customer_id?: string | null;
  username: string;
  customer_name?: string | null;
  account_number: string;
  phone_number?: string | null;
  email?: string | null;
  status: boolean;
  service_expires_at?: string | null;
  expiry_status: "expired" | "expiring_soon";
  days_remaining: number;
  amount_payable?: number | null;
  plan_name?: string | null;
  router_name?: string | null;
  created_at?: string | null;
}

interface ExpiryAlertsResponse {
  ok: boolean;
  bucket: ExpiryTabKey;
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  items: ExpiryCustomer[];
  counts: {
    expired: number;
    day_1: number;
    day_2: number;
    day_3: number;
    total: number;
  };
}

const tabs: Array<{
  key: ExpiryTabKey;
  label: string;
  description: string;
}> = [
  {
    key: "expired",
    label: "Expired",
    description: "Customers whose service date has passed",
  },
  {
    key: "1",
    label: "1 Day",
    description: "Expiring within 24 hours",
  },
  {
    key: "2",
    label: "2 Days",
    description: "Expiring within 48 hours",
  },
  {
    key: "3",
    label: "3 Days",
    description: "Expiring within 72 hours",
  },
];

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

function useDebouncedValue<T>(value: T, delay = 450): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatKES = (value?: number | null) => {
  if (value == null) return "—";
  return `KES ${new Intl.NumberFormat("en-KE").format(Number(value || 0))}`;
};

const displayName = (customer: ExpiryCustomer) =>
  customer.customer_name ||
  customer.username ||
  customer.account_number ||
  "Unknown Customer";

const expiryLabel = (customer: ExpiryCustomer) => {
  if (customer.expiry_status === "expired") return "Expired";

  if (customer.days_remaining === 1) return "1 day left";

  return `${customer.days_remaining} days left`;
};

const ExpiryBadge: React.FC<{ customer: ExpiryCustomer }> = ({ customer }) => {
  const expired = customer.expiry_status === "expired";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
        expired
          ? "border-rose-200 bg-rose-100 text-rose-700"
          : "border-amber-200 bg-amber-100 text-amber-800"
      }`}
    >
      {expired ? (
        <AlertTriangle className="h-3 w-3" />
      ) : (
        <Clock className="h-3 w-3" />
      )}
      {expiryLabel(customer)}
    </span>
  );
};

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
      active
        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
        : "border-slate-300 bg-slate-200 text-slate-600"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-slate-500"
      }`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-36"
              : index === 4
                ? "w-24 rounded-full"
                : index === 7
                  ? "w-20"
                  : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-36 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>
      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const CustomerExpiryTabs: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ExpiryTabKey>("expired");
  const [items, setItems] = useState<ExpiryCustomer[]>([]);
  const [counts, setCounts] = useState({
    expired: 0,
    day_1: 0,
    day_2: 0,
    day_3: 0,
    total: 0,
  });

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 450);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCount = (key: ExpiryTabKey) => {
    if (key === "expired") return counts.expired || 0;
    return Number((counts as any)[`day_${key}`] || 0);
  };

  const loadAlerts = async () => {
    setError(null);

    if (items.length === 0) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const params = new URLSearchParams();

      params.set("bucket", activeTab);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));

      if (debouncedSearch.trim()) {
        params.set("q", debouncedSearch.trim());
      }

      const res = (await fetchApp(
        `${API_CONFIG.baseUrl}/customers/expiry-alerts?${params.toString()}`,
      )) as ExpiryAlertsResponse;

      setItems(Array.isArray(res.items) ? res.items : []);

      setCounts(
        res.counts || {
          expired: 0,
          day_1: 0,
          day_2: 0,
          day_3: 0,
          total: 0,
        },
      );

      setTotal(Number(res.total || 0));
      setTotalPages(Math.max(Number(res.total_pages || 1), 1));
    } catch (err: any) {
      setError(err?.message || "Failed to load expiry alerts.");
      setItems([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, pageSize]);

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, pageSize, debouncedSearch]);

  const stats = useMemo(
    () => ({
      expired: counts.expired || 0,
      expiringSoon:
        Number(counts.day_1 || 0) +
        Number(counts.day_2 || 0) +
        Number(counts.day_3 || 0),
      total: counts.total || 0,
      currentBucket: getCount(activeTab),
    }),
    [counts, activeTab],
  );

  const activeTabMeta = tabs.find((tab) => tab.key === activeTab);

  const clearSearch = () => setSearch("");

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const openCustomer = (customer: ExpiryCustomer) => {
    navigate(`/customers/${customer.secret_id}`);
  };

  if (error && !loading && items.length === 0) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={loadAlerts}
              className={`${btnBase} border border-rose-200 bg-rose-100 px-3 py-2 text-rose-700 hover:bg-rose-200`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-amber-500 to-emerald-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-rose-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-amber-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-amber-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  Customer Expiry Monitoring
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Customer Expiry Alerts
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Track expired customers and customers whose service expires
                  within the next 1, 2, or 3 days.
                </p>

                {activeTabMeta && (
                  <p className="mt-2 inline-flex rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-[11.5px] font-bold text-slate-500">
                    Current view: {activeTabMeta.description}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={loadAlerts}
                disabled={loading || refreshing}
                className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {loading || refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {loading || refreshing ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing expiry alerts…
          </div>
        )}

        {error && items.length > 0 && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Expired"
            value={stats.expired}
            sub="Already past service expiry"
            icon={<AlertTriangle className="h-5 w-5" />}
            tone="rose"
          />

          <StatCard
            label="Expiring Soon"
            value={stats.expiringSoon}
            sub="Within the next 3 days"
            icon={<CalendarClock className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Current Bucket"
            value={stats.currentBucket}
            sub={activeTabMeta?.label || "Selected tab"}
            icon={<Clock className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Total Alerts"
            value={stats.total}
            sub="All expiry alert records"
            icon={<User className="h-5 w-5" />}
            tone="emerald"
          />
        </div>

        {/* Main Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <CalendarClock className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Expiry Alert List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  {loading
                    ? "Loading expiry alerts…"
                    : `${items.length} of ${total} customers`}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, account, phone, email…"
                  className={`${inputCls} pl-10`}
                />
              </div>

              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {tabs.map((tab) => {
                const active = activeTab === tab.key;
                const count = getCount(tab.key);

                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-2xl border px-3.5 py-2 text-[12px] font-black transition-all ${
                      active
                        ? tab.key === "expired"
                          ? "border-rose-200 bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-md shadow-rose-500/20"
                          : "border-amber-200 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/20"
                        : "border-slate-300 bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                    }`}
                  >
                    {tab.label}

                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                        active
                          ? "bg-white/20 text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Customer</th>
                  <th className="px-5 py-3.5 font-black">Account</th>
                  <th className="px-5 py-3.5 font-black">Contact</th>
                  <th className="px-5 py-3.5 font-black">Plan</th>
                  <th className="px-5 py-3.5 font-black">Expiry</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 text-right font-black">Amount</th>
                  <th className="px-5 py-3.5 text-right font-black">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : items.length > 0 ? (
                  items.map((customer) => (
                    <tr
                      key={customer.secret_id}
                      className="cursor-pointer transition-colors hover:bg-amber-50/60"
                      onClick={() => openCustomer(customer)}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[12px] font-black text-amber-700 ring-1 ring-amber-200">
                            {displayName(customer).slice(0, 2).toUpperCase()}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-black text-slate-900">
                              {displayName(customer)}
                            </p>

                            <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                              {customer.username}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-600">
                        {customer.account_number}
                      </td>

                      <td className="px-5 py-4">
                        <p className="text-[12.5px] font-bold text-slate-700">
                          {customer.phone_number || "—"}
                        </p>

                        <p className="mt-0.5 text-[11px] font-medium text-slate-400">
                          {customer.email || "No email"}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        {customer.plan_name ? (
                          <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                            {customer.plan_name}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <ExpiryBadge customer={customer} />

                        <p className="mt-1 text-[11px] font-medium text-slate-400">
                          {formatDate(customer.service_expires_at)}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                          <Router className="h-3 w-3" />
                          {customer.router_name || "—"}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-slate-800">
                        {formatKES(customer.amount_payable)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCustomer(customer);
                          }}
                          className={`${btnBase} border border-slate-300 bg-slate-50 px-3 py-2 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <CheckCircle2 className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No customers found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        There are no customers in this expiry bucket.
                      </p>

                      {search && (
                        <button
                          type="button"
                          onClick={clearSearch}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : items.length > 0 ? (
              items.map((customer) => (
                <button
                  key={customer.secret_id}
                  type="button"
                  onClick={() => openCustomer(customer)}
                  className="w-full px-4 py-4 text-left transition-colors hover:bg-amber-50 active:bg-amber-100"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[13.5px] font-black text-slate-900">
                        {displayName(customer)}
                      </p>

                      <p className="mt-0.5 truncate font-mono text-[11.5px] font-bold text-slate-500">
                        {customer.account_number}
                      </p>

                      {customer.plan_name && (
                        <span className="mt-2 inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[10.5px] font-black text-sky-800">
                          {customer.plan_name}
                        </span>
                      )}
                    </div>

                    <ExpiryBadge customer={customer} />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Expires
                      </p>

                      <p className="mt-1 text-[12.5px] font-black text-slate-900">
                        {formatDate(customer.service_expires_at)}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                        Amount
                      </p>

                      <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                        {formatKES(customer.amount_payable)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Contact
                    </p>

                    <p className="mt-1 text-[12px] font-bold text-slate-700">
                      {customer.phone_number || "No phone"}
                    </p>

                    <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                      {customer.email || "No email"}
                    </p>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      <Router className="h-3 w-3" />
                      {customer.router_name || "No router"}
                    </span>

                    <StatusBadge active={customer.status} />
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <CheckCircle2 className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No customers found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  There are no customers in this expiry bucket.
                </p>

                {search && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer / Pagination */}
          <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <p className="text-[12px] font-medium text-slate-500">
                Page <span className="font-black text-slate-800">{page}</span>{" "}
                of{" "}
                <span className="font-black text-slate-800">{totalPages}</span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="font-black text-slate-800">{total}</span>{" "}
                customers
              </p>

              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-9 rounded-2xl border border-slate-300 bg-slate-50 px-3 text-[12px] font-bold text-slate-600 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
              <button
                type="button"
                disabled={!canPrev || loading || refreshing}
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>

              <button
                type="button"
                disabled={!canNext || loading || refreshing}
                onClick={() =>
                  setPage((current) => Math.min(current + 1, totalPages))
                }
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerExpiryTabs;
